import React, { useEffect, useMemo, useState } from 'react';
import { FilterContainer } from './styles';
import {
  IFabricationProject,
  IFabricationProjectsState,
  EProjectRevisionStatus,
  EProjectStructure
} from '../../../../../store/ducks/fabrication-projects/types';
import { useDispatch } from 'react-redux';
import { Title, Select, Button, SearchField } from '@gerdau/hefesto';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { useHistory } from 'react-router-dom';
import { useOnFilter } from './useOnFilterProjects';

interface IProjectsFilterProps {
  projectState: IFabricationProjectsState;
  filteredProjects: IFabricationProject[];
  setFilteredProjects(filteredProjects: IFabricationProject[]): void;
  searchInput: string;
  setSearchInput(value: string): void;
  onCleanFilters(): void;
}

const ProjectsFilter: React.FC<IProjectsFilterProps> = ({
  projectState,
  setFilteredProjects,
  searchInput,
  setSearchInput,
  onCleanFilters
}) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { screenName = '', itemDescription = '' } = getTaggingInformationScreen(
    history.location.pathname
  );
  const [mockupValue, setMockupValue] = useState<string>();
  const [structureValue, setStructureValue] = useState<
    EProjectStructure | string
  >();
  const [statusOfRevisionValue, setStatusOfRevisionValue] =
    useState<EProjectRevisionStatus>(EProjectRevisionStatus.ALL);

  const getMockups = (): TSelectOptionProps[] => {
    const mockups: TSelectOptionProps[] = [];
    projectState.projects?.forEach((p) => {
      if (p.mockupId && !mockups.find((m) => m.value === p.mockupName)) {
        if (p.mockupName) {
          mockups.push({
            value: p.mockupName,
            label: p.mockupName
          });
        }
      }
    });
    mockups.push({ label: 'Todos', value: 'Todos' });
    return mockups;
  };

  const mockupsOptions = useMemo(() => getMockups(), [projectState.projects]);

  const getStructures = (): TSelectOptionProps[] => {
    const structures: TSelectOptionProps[] = [];
    projectState.projects?.forEach((p) => {
      p.structures.forEach((struc) => {
        if (struc.id && !structures.find((s) => s.value === struc.name)) {
          structures.push({
            value: struc.name,
            label: struc.name
          });
        }
      });
    });

    structures.push({
      value: 'Todos',
      label: 'Todos'
    });
    return structures;
  };

  const structuresOptions = useMemo(
    () => getStructures(),
    [projectState.projects]
  );

  const statusOptions = useMemo(
    () =>
      Object.values(EProjectRevisionStatus).map((revisionStatus) => ({
        label: revisionStatus,
        value: revisionStatus
      })),
    [EProjectRevisionStatus]
  );

  const cleanFilters = () => {
    dispatch(
      sendTagging({
        screenName,
        event: 'click',
        itemName: 'button: limpar_filtros',
        itemDescription: `${itemDescription}_projetos_limparFiltros`,
        filterInfo: `#stt:${statusOfRevisionValue}#flt:${searchInput}#mqt:${mockupValue}#ett:${structureValue}`
      })
    );
    cleanFilterInputs();
    onCleanFilters();
  };

  const cleanFilterInputs = () => {
    setSearchInput('');
    setMockupValue(undefined);
    setStructureValue(undefined);
    setStatusOfRevisionValue(EProjectRevisionStatus.ALL);
  };

  useEffect(() => {
    if (projectState.loading) {
      cleanFilterInputs();
    }
  });

  const {
    handleFilterByMockup,
    handleFilterBySearchInput,
    handleFilterByStatusOfRevision,
    handleFilterByStructure
  } = useOnFilter();

  const onFilter = (
    newSearchInputValue: string,
    newMockupValue: string | undefined,
    newStructureValue: string | undefined,
    newStatusOfRevisionValue: EProjectRevisionStatus
  ) => {
    let newFilteredProjects = projectState.projects || [];

    setSearchInput(newSearchInputValue);
    newFilteredProjects = handleFilterBySearchInput(
      newSearchInputValue.toUpperCase(),
      newFilteredProjects
    );

    if (newMockupValue && newMockupValue !== 'Todos') {
      newFilteredProjects = handleFilterByMockup(
        newMockupValue,
        newFilteredProjects
      );
    }

    if (newStructureValue && newStructureValue !== 'Todos') {
      newFilteredProjects = handleFilterByStructure(
        newStructureValue,
        newFilteredProjects
      );
    }

    if (newStatusOfRevisionValue !== EProjectRevisionStatus.ALL) {
      setStatusOfRevisionValue(newStatusOfRevisionValue);
      newFilteredProjects = handleFilterByStatusOfRevision(
        newStatusOfRevisionValue,
        newFilteredProjects
      );
    }

    setMockupValue(newMockupValue);
    setStructureValue(newStructureValue);
    setFilteredProjects(newFilteredProjects);
  };

  return (
    <FilterContainer>
      <Title level={3}>Filtros</Title>
      <div className="inputContainer">
        <SearchField
          name="pesquisar"
          label="Pesquisar"
          placeholder="Pesquise um arquivo"
          inputValue={searchInput}
          onChange={(e) =>
            onFilter(
              e.target.value,
              mockupValue,
              structureValue,
              statusOfRevisionValue
            )
          }
        ></SearchField>
        <Select
          label="Maquete"
          name="maquete"
          value={mockupValue}
          options={mockupsOptions}
          onSelect={(value) =>
            onFilter(
              searchInput,
              value as string,
              structureValue,
              statusOfRevisionValue
            )
          }
        ></Select>
        <Select
          label="Estrutura do projeto"
          name="estrutura-do-projeto"
          options={structuresOptions}
          value={structureValue}
          onSelect={(value) =>
            onFilter(
              searchInput,
              mockupValue,
              value as string,
              statusOfRevisionValue
            )
          }
        ></Select>
        <Select
          label="Status da revisão"
          name="status-da-revisão"
          options={statusOptions}
          value={statusOfRevisionValue}
          onSelect={(value) =>
            onFilter(
              searchInput,
              mockupValue,
              structureValue,
              value as EProjectRevisionStatus
            )
          }
        ></Select>
        <div className="button-container">
          <Button name="limpar" variant="neutral" onClick={cleanFilters}>
            Limpar filtros
          </Button>
        </div>
      </div>
    </FilterContainer>
  );
};

export default ProjectsFilter;
